import React, {memo, useEffect, useRef, useState} from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Swal from 'sweetalert2';
import {formFields, dependentFields} from './fieldConfig';
import {EyeIcon, EyeOffIcon, PlusCircleIcon, MinusCircleIcon} from 'lucide-react';
import {useProfileForm} from './useProfileForm';
import useApi from "../../utils/useApi";
import clsx from 'clsx';
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import {useTheme} from "../ThemeContext/ThemeContext";
import TaxQuestionnaire from "../TaxQuestionnaire/TaxQuestionnaire";
import {useNavigate} from "react-router-dom";
import {getFieldLabel} from "./validation";
import {useNavVisibility} from "../NavVisibilityContext/NavVisibilityContext";
import DependentFields from "../Admin/ClientForm/DependentFields";
import {useTaxYear} from "../../contexts/TaxYearContext";
import SelectTaxYear from "../Admin/SelectTaxYear/SelectTaxYear";


const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Return empty if no digits
    if (!digits.length) return '';

    // Format based on number of digits
    if (digits.length <= 3) {
        return digits;
    } else if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
};

const formatSSN = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Return empty if no digits
    if (!digits.length) return '';

    // Format based on number of digits
    if (digits.length <= 3) {
        return digits;
    } else if (digits.length <= 5) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
    }
};

const FormField = React.memo(({
                                  field,
                                  name,
                                  value,
                                  onBlur,
                                  onChange,
                                  showPassword,
                                  onTogglePassword,
                                  error
                              }) => {
    const { isDark } = useTheme();

    const getStringValue = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') {
            if (val instanceof Date) {
                return val.toISOString().split('T')[0];
            }
            return '';
        }
        return String(val);
    };

    const stringValue = getStringValue(value);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        let formattedValue = newValue;

        // Apply formatting based on field type/name
        if (field.type === 'tel' || name.includes('phone')) {
            formattedValue = formatPhoneNumber(newValue);
        } else if (name.includes('ssn')) {
            formattedValue = formatSSN(newValue);
        }

        onChange({
                     target: {
                         name: e.target.name,
                         value: formattedValue
                     }
                 });
    };

    const baseInputClasses = clsx(
        'w-full rounded-md border transition-colors duration-200',
        'focus:ring-2 focus:ring-offset-2 sm:text-sm px-3 py-2',
        'dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500',
        {
            'border-red-500': error
        }
    );

    const renderErrors = () => {
        if (!error) return null;

        const errors = Array.isArray(error) ? error : [error];

        return (
            <div className="mt-1 space-y-1">
                {errors.map((err, index) => (
                    <p key={index} className="text-sm text-red-500 flex items-start">
                        <span className="mr-2">•</span>
                        <span>{err}</span>
                    </p>
                ))}
            </div>
        );
    };

    const renderField = () => {
        switch (field.type) {
            case 'select':
                return (
                    <select
                        name={name}
                        value={stringValue}
                        onChange={onChange}
                        onBlur={onBlur}
                        className={baseInputClasses}
                        required={field.required}
                    >
                        {field.options?.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                );

            case 'textarea':
                return (
                    <textarea
                        name={name}
                        value={stringValue}
                        onChange={onChange}
                        onBlur={onBlur}
                        rows={field.rows || 3}
                        className={baseInputClasses}
                        required={field.required}
                    />
                );

            case 'date':
                const parseLocalDate = (v) => {
                    if (!v) return null;
                    const [y, m, d] = v.split('-').map(Number);
                    return new Date(y, m - 1, d);
                };
                const formatLocalDate = (date) => {
                    if (!date) return '';
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    return `${y}-${m}-${d}`;
                };
                return (
                    <DatePicker
                        selected={parseLocalDate(value)}
                        onBlur={onBlur}
                        onChange={date => onChange({
                                                       target: {
                                                           name,
                                                           value: formatLocalDate(date)
                                                       }
                                                   })}
                        className={baseInputClasses}
                        required={field.required}
                        maxDate={field.maxDate}
                        showYearDropdown
                        yearDropdownItemNumber={100}
                        scrollableYearDropdown
                    />
                );

            case 'password':
                return (
                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            name={name}
                            value={stringValue}
                            onChange={name.includes('ssn') ? handleInputChange : onChange}
                            onBlur={onBlur}
                            className={baseInputClasses}
                            required={field.required}
                            minLength={field.minLength}
                            maxLength={field.maxLength}
                            pattern={field.pattern}
                        />
                        {field.toggleable && (
                            <button
                                type="button"
                                onClick={onTogglePassword}
                                className={clsx(
                                    'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md',
                                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                )}
                            >
                                {showPassword ?
                                 <EyeOffIcon className="h-4 w-4"/> :
                                 <EyeIcon className="h-4 w-4"/>
                                }
                            </button>
                        )}
                    </div>
                );

            default:
                return (
                    <input
                        type={field.type}
                        name={name}
                        value={stringValue}
                        onChange={field.type === 'tel' || name.includes('phone') ? handleInputChange : onChange}
                        onBlur={onBlur}
                        className={baseInputClasses}
                        required={field.required}
                        readOnly={field.readOnly}
                        minLength={field.minLength}
                        maxLength={field.maxLength}
                        pattern={field.pattern}
                    />
                );
        }
    };

    const labelText = typeof field.label === 'object' ? '' : String(field.label || '');
    const helpText = typeof field.help === 'object' ? '' : String(field.help || '');

    return (
        <div className="space-y-1">
            <label
                htmlFor={name}
                className={clsx(
                    'block text-sm font-medium dark:text-gray-200 text-gray-700',
                )}
            >
                {labelText}
                {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderField()}
            {helpText && (
                <p className={clsx(
                    'text-sm dark:text-gray-400 text-gray-500',
                )}>
                    {helpText}
                </p>
            )}
            {renderErrors()}
        </div>
    );
});



const Profile = ({handleShowHeader, handleShowSettingsHeader}) => {
    const { taxYear, updateTaxYear } = useTaxYear();
    const { setIsNavVisible } = useNavVisibility();
    const { isDark } = useTheme();
    const { getProfile, updateProfile } = useApi();
    const navigate = useNavigate();
    const {
        generateUniqueId,
        formData,
        setFormData,
        dependents,
        errors,
        showPassword,
        handleInputChange,
        togglePasswordVisibility,
        resetForm,
        isValid,
        isDirty
    } = useProfileForm();

    const [customer, setCustomer] = useState(null);
    const [preparers, setPreparers] = useState(null);
    const [availableYears, setAvailableYears] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        setIsNavVisible(true);
        initializeProfile();
    }, [taxYear]);

    const initializeProfile = async () => {
        setLoading(true);
        try {
            let data = {
                year: taxYear,
            }
            const response = await getProfile(data);
            setAvailableYears(response.availableYears);
            initializeData(response);
        }
        catch (error) {
            showErrorAlert(['Failed to load profile data']);
        }
        finally {
            setLoading(false);
        }
    };

    const initializeData = (response) => {
        populateClient(response.customer);

        initializePreparers(response.preparers);
    }

    const initializePreparers = (preparers) => {
        setPreparers(preparers);
    }

    const populateClient = (customer) => {
        setCustomer(customer);


        let data = {
            name: customer?.client_id || '',
            account_type: customer?.profile?.account_type || '',
            ssn_id: formatSSN(customer?.profile?.ssn_id) || '',
            client: customer?.profile?.client || '',
            contact_primary: customer?.profile?.contact_primary || '',
            phone_primary: formatPhoneNumber(customer?.profile?.phone_primary) || '',
            phone_secondary: formatPhoneNumber(customer?.profile?.phone_secondary) || '',
            client_dob: customer?.profile?.client_dob || '',
            occupation: customer?.profile?.occupation || '',
            email: customer?.email || '',
            address: customer?.profile?.address || '',
            filing_status: (customer.client_progress.filing_status || customer?.profile?.filing_status) || '',
            spouse_name: customer?.profile?.spouse_name || '',
            spouse_dob: customer?.profile?.spouse_dob || '',
            spouse_ssn: customer?.profile?.spouse_ssn || '',
            spouse_email: customer?.profile?.spouse_email || '',
            spouse_occupation: customer?.profile?.spouse_occupation || '',
            dependents: customer?.client_progress?.dependents || 0,
            state_return: customer?.profile?.state_return || '',
            st_return: customer?.profile?.st_return || '',
            rt_number: customer?.profile?.rt_number || '',
            act_number: customer?.profile?.act_number || '',
            bank_name: customer?.profile?.bank_name || '',
            new_client: customer?.client_progress?.new_client > -1 ? customer?.client_progress?.new_client: '',
            hear_aboutus: customer?.profile?.hear_aboutus || '',
            referral: customer?.profile?.referral || '',
            preferred_preparer: customer?.client_progress?.preparer?.id || '',
            document_copies: customer?.profile?.document_copies || '',
            year: taxYear
        };


        data = setDependents(customer.profile, data);

        setFormData(data);

    };

    const setDependents = (data, initialData) => {
        for(let x = 1; x <= 10; x++) {
            let dependent = {};

            if(data[`dep${x}_name`]){
                initialData[`dep${x}_name`] = data[`dep${x}_name`];
                initialData[`dep${x}_dob`] = data[`dep${x}_dob`];
                initialData[`dep${x}_rel`] = data[`dep${x}_rel`];
                initialData[`dep${x}_ssn`] = data[`dep${x}_ssn`];
            }
        }

        return initialData;
    }

    const formatPhoneNumber = (value) => {
        if(!value){
            return '';
        }
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length >= 10) {
            return cleaned.match(/(\d{3})(\d{3})(\d{4})/).slice(1).join('-');
        }
        return cleaned;
    };

    const formatSSN = (value) => {
        if(!value){
            return '';
        }
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length >= 9) {
            return cleaned.match(/(\d{3})(\d{2})(\d{4})/).slice(1).join('-');
        }
        return cleaned;
    };

    const handleSpecialInputs = (e) => {
        const { name, value } = e.target;
        let formattedValue = value;

        if (name.includes('phone')) {
            formattedValue = formatPhoneNumber(value);
        } else if (name.includes('ssn')) {
            formattedValue = formatSSN(value);
        }

        handleInputChange({
                              target: {
                                  name,
                                  value: formattedValue
                              }
                          });
    };

    const showSuccessAlert = () => {
        Swal.fire({
                      title: 'Success!',
                      text: 'Your profile has been updated successfully.',
                      icon: 'success',
                      confirmButtonText: 'Continue',
                      confirmButtonColor: '#3085d6'
                  }).then((result) => {
            if (result.isConfirmed) {
                window.location.href = '/client_home';
            }
        });
    };

    const showErrorAlert = (errors) => {
        Swal.fire({
                      title: 'Error!',
                      html: Array.isArray(errors)
                            ? errors.map(error => `• ${error}<br>`).join('')
                            : 'An error occurred while updating your profile.',
                      icon: 'error',
                      confirmButtonText: 'Ok',
                      confirmButtonColor: '#d33'
                  });
    };

    // ... rest of the initialization and helper functions ...

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isValid()) {
            showErrorAlert(['Please fix the errors before submitting']);
            return;
        }

        setLoading(true);

        try {
            const formDataToSend = new FormData();

            // Append main form data (convert null to empty string for FormData)
            Object.keys(formData).forEach(key => {
                formDataToSend.append(key, formData[key] ?? '');
            });

            console.log(dependents);

            // Append dependents data
            dependents.forEach((dep, index) => {
                Object.keys(dep).forEach(field => {
                    formDataToSend.append(`dep${index + 1}_${field}`, dep[field]);
                });
            });

            const response = await updateProfile(customer.id, formDataToSend);

            console.log(response);

            if (!response.customer) {
                throw new Error(response.message || 'Failed to update profile');
            }

            initializeData(response);

            await Swal.fire({
                                title: 'Success!',
                                text: 'Profile updated successfully',
                                icon: 'success',
                                confirmButtonColor: '#3085d6',
                                confirmButtonText: 'Continue'
                            });

            navigate('/');
        }
        catch (error) {
            await Swal.fire({
                                title: 'Error',
                                text: error.message || 'Failed to update profile',
                                icon: 'error',
                                confirmButtonColor: '#d33'
                            });
        }
        finally {
            setLoading(false);
        }
    };

    const renderErrorSummary = () => {
        if (!errors || Object.keys(errors).length === 0) return null;

        const errorMessages = [];

        // Get field errors
        Object.entries(errors).forEach(([fieldName, fieldErrors]) => {
            if (fieldName === 'dependents') {
                // Handle dependent errors separately
                const dependentErrors = fieldErrors;
                dependentErrors.forEach((depErrors, index) => {
                    if (depErrors) {
                        Object.entries(depErrors).forEach(([field, error]) => {
                            errorMessages.push(`Dependent ${index + 1} ${field}: ${error}`);
                        });
                    }
                });
            } else {
                // Handle regular field errors
                const label = getFieldLabel(fieldName);
                if (Array.isArray(fieldErrors)) {
                    fieldErrors.forEach(error => {
                        errorMessages.push(`${label}: ${error}`);
                    });
                }
            }
        });

        if (errorMessages.length === 0) return null;

        return (
            <div className="mt-4 p-4 border border-red-200 rounded-md bg-red-50 dark:bg-red-900/10 dark:border-red-900/30">
                <h4 className="text-sm font-medium text-red-800 dark:text-red-400 mb-2">
                    Please fix the following errors:
                </h4>
                <ul className="space-y-1">
                    {errorMessages.map((error, index) => (
                        <li key={index} className="text-sm text-red-600 dark:text-red-400 flex items-start">
                            <span className="mr-2">•</span>
                            <span>{error}</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    if (loading) {
        return <LoadingScreen/>;
    }


    return (
        <div className={clsx(
            'min-h-screen page-container z-10 relative py-12 dark:bg-gray-900 bg-gray-50',
        )}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8 flex justify-between items-center">
                    <div>
                        <div className="flex flex-row justify-start items-center gap-2">
                            <h1 className={clsx(
                                'text-3xl font-bold dark:text-white text-gray-900',
                            )}>
                                My Profile
                            </h1>
                            <SelectTaxYear selectedYear={taxYear}
                                           availableYears={availableYears}/>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            You are editing your {taxYear} tax profile details
                        </p>
                            {isDirty && (
                                <p className="mt-2 text-sm text-yellow-500">
                                    You have unsaved changes
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Main Form */}
                    <div className={clsx(
                    'rounded-lg shadow overflow-hidden dark:bg-gray-800 bg-white',
                )}>
                    <div className="p-6">
                        <form onSubmit={handleSubmit} className="space-y-8">
                            {Object.entries(formFields).map(([sectionKey, section]) => {

                                if (section.showIf && !section.showIf(formData)) {
                                    return null;
                                }

                                return (
                                    <React.Fragment key={sectionKey}>
                                        <div className="space-y-6">
                                            <div className="flex flex-col justify-start gap-2">
                                                <h3 className={clsx(
                                                    'text-lg font-medium dark:text-white text-gray-900',
                                                )}>
                                                    {section.title}
                                                </h3>
                                                {section.subTitle && (
                                                    <h3 className={clsx(
                                                        'text-sm font-medium dark:text-white text-gray-900',
                                                    )}>
                                                        {section.subTitle}
                                                    </h3>
                                                )}
                                            </div>


                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {Object.entries(section.fields).map(([fieldKey, field]) => {
                                                    if (fieldKey == 'dependentFields') return null;

                                                    let renderDependents = false;
                                                    if (field.showIf && !field.showIf(formData)) {
                                                        return null;
                                                    }

                                                    if (fieldKey === 'preferred_preparer' && field.dynamicOptions) {
                                                        let options = [
                                                            { value: "", label: "Please Select" },

                                                        ];
                                                        preparers?.map(preparer => (
                                                            options.push({
                                                                value: preparer.id,
                                                                label: preparer.profile.client
                                                            })
                                                        ));

                                                        field.options = options;
                                                    }

                                                    if(fieldKey === 'state_return' && formData.dependents == 1 && formFields.personalTaxInfo.fields.dependentFields){
                                                        renderDependents = true
                                                    }

                                                    return (
                                                        <>
                                                            {renderDependents && (
                                                                <div className="space-y-6 w-full col-span-2">
                                                                    {formData.dependents == 1 && (
                                                                        <div className="space-y-6">
                                                                            <h2 id="dependents">Dependents</h2>
                                                                            <DependentFields formData={formData} setFormData={setFormData} required={true}/>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                            <FormField
                                                                key={fieldKey}
                                                                field={field}
                                                                name={fieldKey}
                                                                value={formData[fieldKey]}
                                                                onChange={handleInputChange}
                                                                showPassword={showPassword[fieldKey]}
                                                                onTogglePassword={() => togglePasswordVisibility(fieldKey)}
                                                                error={errors[fieldKey]}
                                                            />
                                                        </>
                                                    );
                                                })}
                                            </div>
                                        </div>


                                    </React.Fragment>
                                );
                            })}

                            {/* Form Actions */}
                            <div className={clsx(
                                'flex justify-between pt-8 border-t dark:border-gray-700 border-gray-200',
                            )}>
                                <div className="gap-3 space-y-1 sm:space-x-4 justify-between">
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        disabled={loading}
                                        className={clsx(
                                            'px-4 py-2 rounded-md transition-colors duration-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white bg-white hover:bg-gray-50 text-gray-700 border border-gray-300',
                                            loading && 'opacity-50 cursor-not-allowed'
                                        )}
                                    >
                                        Reset
                                    </button>
                                    <a
                                        href={`/client/profile/${customer?.id}/printpreview`}
                                        className={clsx(
                                            'px-4 py-2 rounded-md inline-block transition-colors duration-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white bg-white hover:bg-gray-50 text-gray-700 border border-gray-300',
                                        )}
                                    >
                                        Print Form
                                    </a>
                                </div>
                                <button
                                    type="submit"
                                    disabled={!isValid() || loading}
                                    className={clsx(
                                        'px-6 py-2 rounded-md text-white flex items-center space-x-2 transition-colors duration-200',
                                        loading || !isValid()
                                        ? 'bg-indigo-400 cursor-not-allowed'
                                        : 'bg-indigo-600 hover:bg-indigo-700'
                                    )}
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white"/>
                                            <span>Updating...</span>
                                        </>
                                    ) : (
                                         <>
                                             <span>Update Profile</span>
                                             {!isValid() && (
                                                 <span className="text-xs">
                                                (Fix errors to enable)
                                            </span>
                                             )}
                                         </>
                                     )}
                                </button>
                            </div>
                            {renderErrorSummary()}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
